import { motion } from 'framer-motion';
import { Check, Users, Target, Heart, Truck, Clock, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function About() {
  const { t, language } = useLanguage();
  
  const solutions = [
    { text: 'des produits africains de qualité', textEn: 'quality African products' },
    { text: 'des prix très compétitifs', textEn: 'very competitive prices' },
    { text: 'un excellent rapport qualité-prix', textEn: 'excellent value for money' },
    { text: 'une livraison rapide partout en Belgique', textEn: 'fast delivery throughout Belgium' },
  ];
  
  const problems = [
    { text: 'Les produits sont parfois très chers', textEn: 'Products are sometimes very expensive' },
    { text: 'Les magasins sont souvent situés en centre-ville', textEn: 'Stores are often located in city centers' },
    { text: 'Il est difficile de se garer ou de se déplacer', textEn: 'It\'s difficult to park or travel' },
    { text: 'Après le travail, la fatigue rend les courses compliquées', textEn: 'After work, fatigue makes shopping complicated' },
    { text: 'Les parents et les familles manquent de temps', textEn: 'Parents and families lack time' },
  ];
  
  const commitments = [
    { icon: Clock, text: 'vous faire gagner du temps', textEn: 'save you time' },
    { icon: Package, text: 'vous offrir des produits de qualité', textEn: 'offer you quality products' },
    { icon: Truck, text: 'rendre vos courses simples et rapides', textEn: 'make your shopping simple and fast' },
    { icon: Heart, text: 'vous proposer les meilleurs prix possibles', textEn: 'offer you the best possible prices' },
  ];
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-secondary/10 to-accent/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">À propos</span>
            <h1 className="text-3xl lg:text-5xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="about-title">
              {t('aboutTitle')}
            </h1>
            <p className="text-lg lg:text-xl text-text_secondary mt-6 leading-relaxed">
              Bienvenue chez <span className="text-primary font-semibold">AfriOne B&D</span>, votre solution simple et rapide pour accéder à l'alimentation africaine en Belgique 🇧🇪
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Mission */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-widest text-primary font-medium">{t('ourMission')}</span>
              <h2 className="text-3xl font-bold font-heading text-text_primary mt-2 tracking-tight mb-6">
                Rendre les produits africains accessibles à tous
              </h2>
              <p className="text-text_secondary leading-relaxed mb-6">
                Notre mission est de rendre les produits africains accessibles à tous, avec des prix très compétitifs, une excellente qualité et une livraison rapide partout en Belgique.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary/50 rounded-full">
                  <Check className="w-5 h-5 text-secondary_hover" />
                  <span className="text-sm font-medium text-text_primary">Qualité</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-text_primary">Prix compétitifs</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-accent/20 rounded-full">
                  <Check className="w-5 h-5 text-accent" />
                  <span className="text-sm font-medium text-text_primary">Livraison rapide</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.pexels.com/photos/4262183/pexels-photo-4262183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Famille Africaine Partageant un Repas"
                className="w-full rounded-3xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Observation */}
      <section className="py-16 lg:py-24 bg-text_primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{t('ourObservation')}</span>
            <h2 className="text-3xl font-bold font-heading mt-2 tracking-tight">
              Aujourd'hui, accéder à l'alimentation africaine en Europe est souvent compliqué
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white/5 rounded-2xl border border-white/10"
              >
                <span className="text-2xl font-bold text-primary/50">{index + 1}</span>
                <p className="text-white/80 mt-2">
                  {language === 'fr' ? problem.text : problem.textEn}
                </p>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-white/60 mt-8 text-lg">
            Tout cela rend les courses longues, fatigantes et contraignantes.
          </p>
        </div>
      </section>
      
      {/* Our Solution */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{t('ourSolution')}</span>
            <h2 className="text-3xl font-bold font-heading text-text_primary mt-2 tracking-tight">
              Chez AfriOne B&D, nous avons décidé de simplifier votre quotidien
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 p-5 bg-secondary/30 rounded-2xl"
              >
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-secondary_hover" />
                </div>
                <p className="font-medium text-text_primary">
                  {language === 'fr' ? solution.text : solution.textEn}
                </p>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-text_secondary mt-8">
            Vous pouvez désormais faire vos courses sans stress, sans déplacement et sans perte de temps.
          </p>
        </div>
      </section>
      
      {/* Goals & Commitments */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-secondary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Goal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold font-heading text-text_primary mb-4">{t('ourGoal')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">→</span>
                  <span className="text-text_secondary">Permettre à tous les Africains en Belgique d'avoir facilement accès aux produits de chez nous</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">→</span>
                  <span className="text-text_secondary">Offrir des prix accessibles à toutes les familles</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">→</span>
                  <span className="text-text_secondary">Livrer rapidement, où que vous soyez en Belgique</span>
                </li>
              </ul>
            </motion.div>
            
            {/* Commitment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-3xl shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-secondary_hover" />
              </div>
              <h3 className="text-2xl font-bold font-heading text-text_primary mb-4">{t('ourCommitment')}</h3>
              <ul className="space-y-3">
                {commitments.map((commitment, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center flex-shrink-0">
                      <commitment.icon className="w-4 h-4 text-secondary_hover" />
                    </div>
                    <span className="text-text_secondary">
                      {language === 'fr' ? commitment.text : commitment.textEn}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Promise */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary to-primary_hover p-8 lg:p-12 rounded-3xl text-white text-center"
          >
            <h2 className="text-3xl font-bold font-heading mb-6">{t('ourPromise')}</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Avec AfriOne B&D, vous n'avez plus besoin de:
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">❌ chercher une place de parking</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">❌ faire la file en magasin</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">❌ perdre du temps dans les déplacements</span>
            </div>
            <p className="text-2xl font-bold">
              👉 Vous commandez, nous livrons.
            </p>
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/80">Merci pour votre confiance</p>
              <p className="text-xl font-bold mt-2">AfriOne B&D</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
